/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Alert, Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  setDestination,
  nextStep,
  prevStep,
  setStepVerified,
} from '../santeMobiliteSlice';
import { Country } from '../types';
import { listCountriesApi } from '@/api/santeMobiliteApi';
import CountryPingMap from '../components/CountryPingMap';
import { destinationLabel } from '../countryLabel';

// Zone 0 = pays non couvert. Cf. règle métier back ; le pays est quand même
// listé dans /countries pour transparence (l'étudiant peut le voir) mais on
// l'empêche d'aller plus loin.
const isCountryCovered = (c: Country | null): boolean => {
  if (!c) return true;
  const z = c.zone;
  if (z === undefined || z === null) return true;
  const asNumber = typeof z === 'string' ? parseInt(z, 10) : z;
  return asNumber !== 0;
};

const TextfieldStyled = styled(TextField)`
  & .MuiInputBase-root {
    border-radius: 30px;
  }
  & .MuiOutlinedInput-root {
    padding: 4px 12px;
  }
`;

const DestinationStep = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('sante-mobilite');
  const destination = useAppSelector(state => state.santeMobilite.destination);

  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchCountries = async () => {
      setLoading(true);
      try {
        const response = await listCountriesApi();
        const data: Country[] = response.data?.data ?? response.data ?? [];
        if (!cancelled) setCountries(data);
      } catch (err) {
        // L'app gère déjà les toastr d'erreur génériques via interceptors.
        if (!cancelled) setCountries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCountries();
    return () => {
      cancelled = true;
    };
  }, []);

  // Le back ne sert que des noms français. On les traduit à l'affichage — et on
  // trie sur le libellé TRADUIT, dans la locale courante : une liste triée
  // alphabétiquement en français n'a plus aucun sens une fois affichée en
  // chinois ou en arabe.
  const label = (c: Country | null | undefined) =>
    destinationLabel(c?.name, i18n.language);

  const sortedCountries = useMemo(
    () =>
      [...countries].sort((a, b) =>
        label(a).localeCompare(label(b), i18n.language),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countries, i18n.language],
  );

  // On commit la sélection directement en Redux, comme le step Dates : pas de
  // useState intermédiaire. L'utilisateur peut donc naviguer via le stepper
  // après avoir choisi un pays sans avoir à cliquer "Suivant" — sa sélection
  // suit et le prix est invalidé automatiquement par le reducer setDestination.
  const handleSelect = (value: Country | null) => {
    dispatch(setDestination(value));
  };

  const countryCovered = isCountryCovered(destination);

  const handleNext = () => {
    if (!destination || !countryCovered) return;
    dispatch(setStepVerified(2));
    dispatch(nextStep());
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Typography variant="subtitle1" gutterBottom mb={0}>
        {t('step2.title')}
      </Typography>

      <Box mt={3}>
        <Autocomplete
          options={sortedCountries}
          loading={loading}
          getOptionLabel={(o) => label(o)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={destination}
          onChange={(_, value) => handleSelect(value)}
          renderInput={(params) => (
            <TextfieldStyled
              {...params}
              label={t('step2.destination_label')}
              fullWidth
            />
          )}
        />
      </Box>

      {destination && !countryCovered && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('step2.country_not_covered', { country: label(destination) })}
        </Alert>
      )}

      <Box mt={3}>
        <CountryPingMap
          selected={destination}
          countries={countries}
          onCountrySelect={handleSelect}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={() => dispatch(prevStep())} variant="outlined">
          {t('common_actions.back')}
        </Button>
        <Button
          onClick={handleNext}
          disabled={!destination || !countryCovered}
          variant="contained"
        >
          {t('common_actions.next')}
        </Button>
      </Box>
    </Box>
  );
};

export default DestinationStep;
